import { DeckEntryRow } from './DeckEntryRow.js';
import { DeckValidityPill } from './DeckValidityPill.js';
import type { HandCardData } from './HandCard.js';
import type { Format, ValidationResult, DeckSummary } from '../decks/formats.js';
import type { DeckEntry } from '../decks/types.js';

export type DeckSidebarProps = {
  name: string;
  format: Format;
  entries: readonly DeckEntry[];
  pool: readonly HandCardData[];
  summary: DeckSummary;
  validation: ValidationResult;
  onRename: (name: string) => void;
  onIncrement: (cardId: string) => void;
  onDecrement: (cardId: string) => void;
  onPreview: (card: HandCardData) => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
};

const KIND_GROUPS: Array<{ kind: string; label: string }> = [
  { kind: 'player', label: 'Players' },
  { kind: 'action', label: 'Actions' },
  { kind: 'stadium', label: 'Stadium' },
  { kind: 'weather', label: 'Weather' },
];

export function DeckSidebar({
  name,
  format,
  entries,
  pool,
  summary,
  validation,
  onRename,
  onIncrement,
  onDecrement,
  onPreview,
  onDelete,
  onDuplicate,
}: DeckSidebarProps) {
  const byId = new Map(pool.map((c) => [c.id, c] as const));
  const grouped = KIND_GROUPS.map((g) => ({
    ...g,
    rows: entries
      .map((e) => ({ entry: e, card: byId.get(e.cardId) }))
      .filter(
        (row): row is { entry: DeckEntry; card: HandCardData } =>
          !!row.card && row.card.kind === g.kind,
      )
      .sort((a, b) => (a.card.name ?? '').localeCompare(b.card.name ?? '')),
  }));

  const orphans = entries.filter((e) => !byId.has(e.cardId));

  return (
    <aside className="ec-surface flex h-fit flex-col gap-3 p-4">
      <div className="space-y-2">
        <input
          type="text"
          value={name}
          onChange={(e) => onRename(e.target.value)}
          className="w-full rounded-md border border-ec-line bg-ec-ground px-2 py-1.5 text-sm font-medium text-ec-face focus:border-ec-brass focus:outline-none"
          placeholder="Untitled deck"
        />
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-ec-bone-dim">
          <span className="font-mono">{format.name}</span>
          <DeckValidityPill
            validation={validation}
            summary={summary}
            deckSize={format.rules.deckSize}
          />
        </div>
        {!validation.ok && (
          <ul className="space-y-0.5 text-[11px] text-ec-brass">
            {validation.errors.slice(0, 4).map((err, i) => (
              <li key={i}>· {err}</li>
            ))}
            {validation.errors.length > 4 && (
              <li className="font-serif italic text-ec-bone-dim">+ {validation.errors.length - 4} more</li>
            )}
          </ul>
        )}
      </div>

      <div className="ec-rule" />

      {entries.length === 0 ? (
        <p className="py-6 text-center text-sm text-ec-bone-dim">
          Click cards on the left to add them.
        </p>
      ) : (
        <div className="space-y-3">
          {grouped.map((group) =>
            group.rows.length === 0 ? null : (
              <section key={group.kind}>
                <h3 className="mb-1 font-display text-[10px] font-semibold uppercase tracking-widest text-ec-bone-dim">
                  {group.label} · {group.rows.reduce((n, r) => n + r.entry.qty, 0)}
                </h3>
                <ul className="space-y-1">
                  {group.rows.map(({ entry, card }) => (
                    <DeckEntryRow
                      key={entry.cardId}
                      card={card}
                      qty={entry.qty}
                      onIncrement={() => onIncrement(entry.cardId)}
                      onDecrement={() => onDecrement(entry.cardId)}
                      onPreview={() => onPreview(card)}
                    />
                  ))}
                </ul>
              </section>
            ),
          )}
          {orphans.length > 0 && (
            <section>
              <h3 className="mb-1 font-display text-[10px] font-semibold uppercase tracking-widest text-ec-brass">
                Orphan entries
              </h3>
              <ul className="space-y-1">
                {orphans.map((e) => (
                  <li
                    key={e.cardId}
                    className="flex items-center justify-between rounded-md border border-ec-brass/40 bg-[rgba(245,168,28,0.14)] px-2 py-1 text-xs text-ec-brass"
                  >
                    <span className="truncate font-mono">{e.cardId}</span>
                    <button
                      type="button"
                      onClick={() => onDecrement(e.cardId)}
                      className="text-ec-brass hover:text-ec-oxblood-2"
                    >
                      ✕
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}

      {(onDuplicate || onDelete) && (
        <>
          <div className="ec-rule" />
          <div className="flex gap-2">
            {onDuplicate && (
              <button
                type="button"
                onClick={onDuplicate}
                className="ec-btn !py-1.5 flex-1 text-xs"
              >
                Duplicate
              </button>
            )}
            {onDelete && (
              <button
                type="button"
                onClick={onDelete}
                className="flex-1 rounded-md border border-ec-oxblood bg-[rgba(39,147,211,0.28)] py-1.5 font-display text-xs text-ec-face hover:border-ec-oxblood-2"
              >
                Delete
              </button>
            )}
          </div>
        </>
      )}
    </aside>
  );
}
